import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { get } from "lodash";

import { queryClient } from "App";
import { getEmailScheduleTableColumns } from "routes/campaign/components/email_schedule_table/EmailScheduleTableColumnsAndFilters";
import { EMAIL_SCHEDULE_TABLE_CONSTANTS } from "routes/campaign/components/email_schedule_table/EmailScheduleTableV3";
import CampaignScheduleService from "services/CampaignScheduleService";
import UserService from "services/User";
import { addResizedColumns } from "hooks/useTableConfig";
import ConstantContactCampaignsService from "services/ConstantContactCampaigns";
import { useSocket } from "providers/SocketProvider";
import { emailScheduleKeys } from "routes/campaign/components/email_schedule_table/queryKeys";

export const CONSTANT_CONTACT_SYNC_STATUS = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  failed: "Failed",
};

export const useEmailScheduleTable = ({ campaignId = null }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });
  const { socket } = useSocket();

  const queryKey = useMemo(
    () => emailScheduleKeys.list({ campaignId }),
    [campaignId]
  );

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const handleDeleteEmailSchedule = useCallback(
    async (campaignId, scheduleId) => {
      await CampaignScheduleService.deleteSchedule(campaignId, scheduleId);
      await invalidateData();
    },
    [invalidateData]
  );

  const handleSyncData = useCallback(
    async (scheduleId, templateOnly, contactsOnly) => {
      try {
        await ConstantContactCampaignsService.syncSchedule(
          scheduleId,
          templateOnly,
          contactsOnly
        );
        await invalidateData();
      } catch (error) {
        console.error(error);
      }
    },
    [invalidateData]
  );

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getEmailScheduleTableColumns({
      campaignId,
      handleSyncData,
      invalidateData,
      handleDeleteEmailSchedule,
    });
    const emailScheduleTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${EMAIL_SCHEDULE_TABLE_CONSTANTS.tableName}`,
      {}
    );

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: emailScheduleTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    tableSettings,
    isTableSettingsFetched,
    campaignId,
    invalidateData,
    handleSyncData,
    handleDeleteEmailSchedule,
  ]);

  useEffect(() => {
    if (socket) {
      socket.on("email_schedule_synced", invalidateData);
    }

    return () => {
      if (socket) {
        socket.off("email_schedule_synced", invalidateData);
      }
    };
  }, [socket, invalidateData]);

  return { columns, queryKey, invalidateData };
};
